import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BorderRadiusProps,
  ColorProps,
  DisplayProps,
  SizeProps,
  WidthProps,
} from 'styled-system';
import {
  borderRadius,
  color,
  display,
  space,
  size as systemSize,
  themeGet,
  width,
} from 'styled-system';

import type { Color } from 'common/theme/colors/types';
import starEmpty from '../../assets/icons/star-empty.svg?sprite';
import star from '../../assets/icons/star.svg?sprite';
import { useKeyboardSelect } from '../../hooks/keyboard';
import { useLocalization } from '../../providers/LocaleProvider';
import type { FillProps, SpaceProps } from '../../theme/system-types';
import Icon from '../Icon';

interface StarProps
  extends SizeProps, SpaceProps, BorderRadiusProps, DisplayProps, AlignItemsProps {
  isFull?: boolean;
  readonly?: boolean;
  starIndex?: number;
  tabIndex?: number;
  fillColor?: string;
  onChange?: (value: number) => void;
}

const ContainerBase = styled.div<ColorProps>`
  ${color};
`;

const ContainerWithBackground = styled(ContainerBase)<
  SpaceProps & BorderRadiusProps & WidthProps & ColorProps
>`
  ${width};
  ${space};
  ${borderRadius};

  &:hover {
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
  }
  &:active {
    background-color: ${themeGet('colors.sys.neutral.background.active')};
  }
  &:focus-visible {
    outline-color: ${themeGet('colors.sys.secondary.border.default')};
    outline-offset: -${themeGet('space.sp_2')}px;
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
  }
`;
const containerWithBackgroundStyles: ComponentProps<typeof ContainerWithBackground> = {
  width: 'fit-content',
  borderRadius: 'circle',
  padding: 'sp_10',
  mr: 'sp_4',
};

const StyledIcon = styled(Icon)<DisplayProps & SizeProps & SpaceProps & FillProps>`
  ${space};
  ${display};
  ${systemSize};
  ${space};
`;

const styledIconStyles: Partial<ComponentProps<typeof StyledIcon>> = {
  display: 'inline-block',
  mr: ['sp_4'],
};

const Star = ({
  size,
  isFull,
  readonly = true,
  starIndex,
  tabIndex,
  fillColor = 'sys.neutral.icon.default',
  onChange,
  ...restProps
}: StarProps) => {
  const { t, r } = useLocalization();
  const handleKeyboardSelect = useCallback(() => {
    onChange(starIndex);
  }, [starIndex]);
  const handleKeyDown = useKeyboardSelect(handleKeyboardSelect);

  const Container = readonly ? ContainerBase : ContainerWithBackground;

  return (
    <Container
      {...(!readonly
        ? {
            ...containerWithBackgroundStyles,
            tabIndex,
            role: 'radio',
            'aria-label': r(t('h24_stars_amount'), { rating: starIndex }),
            'aria-checked': isFull,
            'aria-hidden': tabIndex === -1,
            onKeyDown: handleKeyDown,
          }
        : {})}
    >
      <StyledIcon
        {...styledIconStyles}
        {...restProps}
        {...(!readonly ? { mr: 'sp_0' } : {})}
        {...(fillColor ? { fill: fillColor as Color } : {})}
        size={size}
        icon={isFull ? star : starEmpty}
      />
    </Container>
  );
};

export default Star;
